import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserData } from '../types/auth';

interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  userData: UserData | null;
  resetPasswordData: {
    verifiedId: string;
    verifiedPhone: string;
  } | null;
}

interface AuthActions {
  signIn: (tokens: { accessToken: string; refreshToken: string }, userData: UserData) => void;
  signOut: () => void;
  updateTokens: (tokens: { accessToken: string; refreshToken?: string }) => void;
  updateUserData: (userData: Partial<UserData>) => void;
  setResetPasswordData: (data: { verifiedId: string; verifiedPhone: string } | null) => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  userData: null,
  resetPasswordData: null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      signIn: (tokens, userData) => {
        set({
          isAuthenticated: true,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          userData,
        });
      },
      
      signOut: () => {
        set(initialState);
      },
      
      updateTokens: (tokens) => {
        const current = get();
        set({
          ...current,
          accessToken: tokens.accessToken,
          ...(tokens.refreshToken && { refreshToken: tokens.refreshToken }),
        });
      },

      updateUserData: (newUserData) => {
        const current = get();
        set({
          ...current,
          userData: current.userData ? { ...current.userData, ...newUserData } : null,
        });
      },

      setResetPasswordData: (data) => {
        set((state) => ({
          ...state,
          resetPasswordData: data,
        }));
      },
    }),
    {
      name: 'aivy-auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        userData: state.userData,
        resetPasswordData: state.resetPasswordData,
      }),
    }
  )
);

export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useUserData = () => useAuthStore((state) => state.userData);
export const useAccessToken = () => useAuthStore((state) => state.accessToken); 
export const useResetPasswordData = () => useAuthStore((state) => state.resetPasswordData); 